import * as angular from "angular";
import { XeroService } from "../../../../services/xero.service";
import { ToastUtils } from "../../../../helper/toast.helper";

export interface XeroRow {
    status: string;
    xero_contact_id: string | null;
    xero_tenant_id: string | null;
    matched_email: string | null;
    match_source: string | null;
    candidate_ids: string[] | null;
    name: string | null;
    email: string | null;
    phone: string | null;
    account_number: string | null;
    contact_status: string | null;
    outstanding: string | null;
    overdue: string | null;
    currency: string | null;
    details_refreshed_at: string | null;
}

export interface XeroCandidate {
    xero_contact_id: string;
    name: string | null;
    email: string | null;
    contact_status: string | null;
    account_number: string | null;
}

/**
 * AV-17974: the "Xero" section of the account / organisation dialogs.
 * Matched → details + View in Xero + Unlink. Otherwise → editable match
 * email, Find in Xero (one SearchTerm call), paste-an-id, and candidates
 * to pick from when the sweep found several.
 */
let AdminXeroSectionComponent = {
    selector: "vcAdminXeroSection",
    templateUrl:
        "/angular/components/admin/dialogs/sections/admin-xero-section.component.html",
    bindings: {
        campaignid: "<",
        oid: "<",
        /** Sub-accounts view the organisation's link but cannot change it. */
        readonly: "<?",
        /** Organisation name for the read-only note on sub-accounts. */
        orgName: "<?",
        onChange: "&",
    },
    controller: class AdminXeroSectionController {
        // * bindings for typescript compiler *
        public campaignid: any;
        public oid: any;
        public readonly: boolean;
        public orgName: string | null;
        public onChange: (locals: { row: XeroRow | null }) => void;
        // ************************************
        public loading = true;
        public busy = false;
        public error: string | null = null;
        public notConnected = false;
        public row: XeroRow | null = null;
        public email = "";
        public pasteId = "";
        public candidates: XeroCandidate[] = [];
        public exact = false;
        public searched = false;

        private service: XeroService;
        private toast: ToastUtils;

        constructor(XeroService: XeroService, ToastUtils: ToastUtils) {
            this.service = XeroService;
            this.toast = ToastUtils;
        }

        $onInit(): void {
            this.load();
        }

        private key(): { account?: any; organisation?: any } {
            return this.oid
                ? { organisation: this.oid }
                : { account: this.campaignid };
        }

        load(): void {
            this.loading = true;
            this.error = null;
            this.service.contact(this.key()).then(
                (response: object) => {
                    const json = response["data"];
                    this.row = json.data.row;
                    this.email =
                        (this.row && this.row.matched_email) ||
                        json.data.default_email ||
                        "";
                    this.loading = false;
                },
                (failure: any) => this.fail(failure)
            );
        }

        matched(): boolean {
            return !!(
                this.row &&
                this.row.status == "matched" &&
                this.row.xero_contact_id
            );
        }

        ambiguousIds(): string[] {
            return this.row &&
                this.row.status == "ambiguous" &&
                this.row.candidate_ids
                ? this.row.candidate_ids
                : [];
        }

        xeroUrl(): string {
            return this.row && this.row.xero_contact_id
                ? this.service.contactUrl(this.row.xero_contact_id)
                : "";
        }

        matchedBy(): string {
            const email = this.row && this.row.matched_email;
            switch (this.row && this.row.match_source) {
                case "auto":
                    return "Matched automatically by " + email;
                case "email":
                    return "Linked by email lookup on " + email;
                case "manual":
                    return "Linked manually";
                default:
                    return "";
            }
        }

        money(value: string | null): string {
            if (value === null || value === undefined || value === "") {
                return "—";
            }
            return (
                value +
                (this.row && this.row.currency ? " " + this.row.currency : "")
            );
        }

        find(): void {
            if (!this.email || this.busy) return;
            this.busy = true;
            this.error = null;
            this.service.lookup(this.email).then(
                (response: object) => {
                    const json = response["data"];
                    this.candidates = json.data.candidates;
                    this.exact = json.data.exact;
                    this.searched = true;
                    this.busy = false;
                    if (this.exact && this.candidates.length == 1) {
                        this.link(this.candidates[0].xero_contact_id, "email");
                    }
                },
                (failure: any) => this.fail(failure)
            );
        }

        link(contactId: string, source: "email" | "manual"): void {
            const id = (contactId || "").trim();
            if (!id || this.busy) return;
            this.busy = true;
            this.error = null;
            this.service
                .link({
                    ...this.key(),
                    xero_contact_id: id,
                    matched_email: this.email,
                    match_source: source,
                })
                .then(
                    (response: object) => {
                        const json = response["data"];
                        this.row = json.data.row;
                        this.candidates = [];
                        this.searched = false;
                        this.pasteId = "";
                        this.busy = false;
                        this.toast.success("Linked to Xero");
                        this.onChange({ row: this.row });
                    },
                    (failure: any) => this.fail(failure)
                );
        }

        unlink(): void {
            if (this.busy) return;
            this.busy = true;
            this.service.unlink(this.key()).then(
                (response: object) => {
                    const json = response["data"];
                    this.row = json.data.row;
                    this.busy = false;
                    this.toast.success("Xero link removed");
                    this.onChange({ row: this.row });
                },
                (failure: any) => this.fail(failure)
            );
        }

        private fail(failure: any): void {
            this.loading = false;
            this.busy = false;
            const payload = failure && failure.data && failure.data.data;
            const code = payload && payload.code;
            if (
                code == "not_connected" ||
                code == "not_configured" ||
                code == "lapsed"
            ) {
                this.notConnected = true;
                this.error = payload.message;
            } else {
                this.error =
                    (payload && payload.message) || "Xero request failed.";
            }
        }
    },
};

angular
    .module("admin")
    .component(AdminXeroSectionComponent.selector, AdminXeroSectionComponent);
